// Calendar sidebar (CALENDAR-C1, docs/design/2026-10-28-calendar.md §3): the overlay list. Mine,
// the company's calendar and each of my teams', then "Shared with me" (CAL-WORK-LAYERS: work
// calendars and Milestones first, then the people I follow or who shared theirs),
// each with its colour dot and a click to show or hide it (the follow's `shown`). Hover icons on
// a row: colour, share and settings (when the caller manages it), unfollow (not for the
// default ones, which can only be hidden). Then the "Tasks" overlay switch, "+ Follow a
// calendar" and "Subscribe (ICS)".
import {
  CalendarDot,
  cn,
  InfoTip,
  NavList,
  NavRow,
  SidebarCaption,
  SidebarEmpty,
  SidebarPanel,
  Switch,
  type TagChipColor,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import {
  CheckSquareIcon,
  EyeIcon,
  EyeOffIcon,
  PaletteIcon,
  PlusIcon,
  RssIcon,
  Settings2Icon,
  Share2Icon,
  UserMinusIcon,
} from "lucide-react";
import { useState } from "react";
import type { CalendarView } from "../../api/types";
import type { SidebarProps } from "../../extensions/registry";
import { HOVER_STRIP, IconAction } from "../../features/cadence/cadence-parts";
import {
  CalendarSettingsDialog,
  ColorDialog,
  FeedDialog,
  FollowDialog,
  ShareDialog,
} from "../../features/calendar/calendar-dialogs";
import { colorOf } from "../../features/calendar/calendar-model";
import { calendarKeys, calendarsQuery } from "../../features/calendar/calendar-queries";
import { useTasksOverlay } from "../../features/calendar/calendar-toggles";
import { calendarLabel } from "../../features/calendar/event-dialogs";
import { problemText } from "../../features/settings/problem-text";

type Modal =
  | { kind: "follow" }
  | { kind: "feed" }
  | { kind: "share" | "settings" | "color"; calendar: CalendarView };

const ROW =
  "group/row relative mx-2 flex min-h-9 items-center gap-2 rounded-md pr-1 text-sm text-sidebar-foreground hover:bg-muted pointer-coarse:min-h-11";
/** The row's hover icons take no room until shown, so the name keeps its width at rest. */
const ROW_STRIP = cn(
  HOVER_STRIP,
  "hidden group-hover/row:flex group-focus-within/row:flex pointer-coarse:flex",
);

export function CalendarSidebar({ company, children }: SidebarProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery({ ...calendarsQuery(api, co), retry: false });
  const [modal, setModal] = useState<Modal | null>(null);
  const [tasksOn, setTasksOn] = useTasksOverlay(api, co);
  const refresh = () => qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
  const toggle = useMutation({
    mutationKey: calendarKeys.selection(co),
    mutationFn: (v: { id: string; shown: boolean }) =>
      api.client.calendarFollows.update({ params: { co, id: v.id }, body: { shown: v.shown } }),
    // Optimistic: the row and the screen's filter flip at once; the refetch confirms.
    onMutate: (v) => {
      qc.setQueryData(calendarKeys.calendars(co), (old: typeof q.data) =>
        old
          ? { ...old, data: old.data.map((c) => (c.id === v.id ? { ...c, shown: v.shown } : c)) }
          : old,
      );
    },
    onSettled: () => void refresh(),
    onError: (e) =>
      toast.add({ title: problemText(e, "The calendar could not be changed."), type: "error" }),
  });
  const unfollow = useMutation({
    mutationKey: calendarKeys.selection(co),
    mutationFn: (id: string) => api.client.calendarFollows.delete({ params: { co, id } }),
    onSuccess: () => {
      void refresh();
      toast.add({ title: "Unfollowed", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The calendar could not be unfollowed."), type: "error" }),
  });

  const data = q.data;
  const mine = data?.my_calendar_id ?? null;
  const all = data?.data ?? [];
  const byId = new Map(all.map((c) => [c.id, c] as const));
  // CAL-WORK-LAYERS: members see two short lists, no kinds or settings: their own calendars
  // (theirs, the company's, their teams'), then what is shared with them (work calendars,
  // Milestones, people they follow). Owners and admins get Share… on what they manage.
  const layer = (c: CalendarView) => c.kind === "work" || c.kind === "milestones";
  const order = (c: CalendarView) =>
    c.id === mine ? 0 : c.kind === "company" ? 1 : c.kind === "team" ? 2 : 3;
  const own = all
    .filter((c) => !layer(c) && (c.default_follow || c.id === mine))
    .sort((a, b) => order(a) - order(b) || a.name.localeCompare(b.name));
  const sharedOrder = (c: CalendarView) =>
    c.kind === "work" ? 0 : c.kind === "milestones" ? 1 : 2;
  const others = all
    .filter((c) => (layer(c) || (!c.default_follow && c.id !== mine)) && c.followed)
    .sort(
      (a, b) =>
        sharedOrder(a) - sharedOrder(b) ||
        calendarLabel(a, mine).localeCompare(calendarLabel(b, mine)),
    );

  const row = (c: CalendarView) => {
    const label = calendarLabel(c, mine);
    const color: TagChipColor = colorOf(c.id, byId);
    return (
      <li key={c.id} data-slot="calendar-row" className={ROW}>
        <button
          type="button"
          aria-pressed={c.shown}
          aria-label={`${c.shown ? "Hide" : "Show"} ${label}`}
          onClick={() => toggle.mutate({ id: c.id, shown: !c.shown })}
          className="flex min-w-0 flex-1 items-center gap-2.5 self-stretch rounded-md pl-2 text-left focus-visible:outline-2 focus-visible:outline-ring"
        >
          <CalendarDot
            color={color}
            className={cn("size-3 transition-opacity", !c.shown && "opacity-25")}
          />
          <span className={cn("min-w-0 flex-1 truncate", !c.shown && "text-muted-foreground")}>
            {label}
          </span>
        </button>
        <span className={ROW_STRIP}>
          <IconAction
            label={`${c.shown ? "Hide" : "Show"} ${label} calendar`}
            tip={c.shown ? "Hide" : "Show"}
            onClick={() => toggle.mutate({ id: c.id, shown: !c.shown })}
          >
            {c.shown ? <EyeOffIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
          </IconAction>
          <IconAction
            label={`Colour of ${label}`}
            tip="Colour"
            onClick={() => setModal({ kind: "color", calendar: c })}
          >
            <PaletteIcon aria-hidden="true" />
          </IconAction>
          {c.manages ? (
            <>
              <IconAction
                label={`Share ${label}`}
                tip="Share…"
                onClick={() => setModal({ kind: "share", calendar: c })}
              >
                <Share2Icon aria-hidden="true" />
              </IconAction>
              {layer(c) ? null : (
                <IconAction
                  label={`Settings of ${label}`}
                  tip="Settings"
                  onClick={() => setModal({ kind: "settings", calendar: c })}
                >
                  <Settings2Icon aria-hidden="true" />
                </IconAction>
              )}
            </>
          ) : null}
          {!c.default_follow && c.id !== mine ? (
            <IconAction
              label={`Unfollow ${label}`}
              tip="Unfollow"
              danger
              onClick={() => unfollow.mutate(c.id)}
            >
              <UserMinusIcon aria-hidden="true" />
            </IconAction>
          ) : null}
        </span>
      </li>
    );
  };

  return (
    <SidebarPanel title="Calendar">
      <SidebarCaption
        label="My calendar"
        className="mt-2"
        action={
          <InfoTip label="About calendars">
            Personal calendars show as busy to others unless you share them.
          </InfoTip>
        }
      />
      <NavList data-slot="calendar-list">{own.map(row)}</NavList>
      <SidebarCaption
        label="Shared with me"
        action={
          <span className="flex items-center">
            <InfoTip label="About shared calendars">
              Work calendars show what goes live or is due on a board. They never block your time.
            </InfoTip>
            <IconAction
              label="Follow a calendar"
              tip="Follow a calendar"
              onClick={() => setModal({ kind: "follow" })}
            >
              <PlusIcon aria-hidden="true" />
            </IconAction>
          </span>
        }
      />
      {others.length ? (
        <NavList data-slot="calendar-others">{others.map(row)}</NavList>
      ) : q.isSuccess ? (
        <SidebarEmpty things="shared calendars" action="Follow one with +" />
      ) : null}
      <SidebarCaption label="Overlays" />
      <NavList>
        <li className={cn(ROW, "pl-2")}>
          <CheckSquareIcon aria-hidden="true" className="size-[18px] shrink-0 text-faint" />
          <span className="min-w-0 flex-1 truncate">Tasks</span>
          <InfoTip label="About the tasks overlay">
            Your tasks due in view, read-only. Due dates change in Work.
          </InfoTip>
          <Switch
            size="sm"
            aria-label="Show tasks"
            checked={tasksOn}
            onCheckedChange={(on) => setTasksOn(Boolean(on))}
            className="mr-1"
          />
        </li>
      </NavList>
      <NavList className="mt-2">
        <NavRow
          icon={PlusIcon}
          label="Follow a calendar"
          render={<button type="button" onClick={() => setModal({ kind: "follow" })} />}
        />
        <NavRow
          icon={RssIcon}
          label="Subscribe (ICS)"
          render={<button type="button" onClick={() => setModal({ kind: "feed" })} />}
        />
      </NavList>
      {modal?.kind === "follow" ? (
        <FollowDialog
          api={api}
          company={company}
          calendars={all}
          myMembershipId={data?.my_membership_id ?? null}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "feed" ? (
        <FeedDialog api={api} company={company} onClose={() => setModal(null)} />
      ) : null}
      {modal?.kind === "share" ? (
        <ShareDialog
          api={api}
          company={company}
          calendar={modal.calendar}
          label={calendarLabel(modal.calendar, mine)}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "settings" ? (
        <CalendarSettingsDialog
          api={api}
          company={company}
          calendar={modal.calendar}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "color" ? (
        <ColorDialog
          api={api}
          company={company}
          calendar={modal.calendar}
          label={calendarLabel(modal.calendar, mine)}
          current={colorOf(modal.calendar.id, byId)}
          onClose={() => setModal(null)}
        />
      ) : null}
      {children}
    </SidebarPanel>
  );
}
